/**
 * useWaitForCommit: returns waitForCommit(), a promise that settles after
 * React has rendered and committed the state updates queued before the call.
 *
 * Why it exists: App's save reads photos and documents from the implementation
 * it assigns during render (saveCurrentProjectRef). A New revision adds its
 * PDFs and photos with setPhotos/setDocuments from async code and then saves;
 * called straight after the add, that save would still read the render before
 * the add and store the quote without the new files.
 *
 * How: each call queues a resolver and bumps a counter. The counter update is
 * queued behind the caller's own updates, so the render that applies it also
 * applies theirs, and the effect keyed on it settles every waiting caller
 * after that render commits. The counter update always causes a render, so
 * the promise settles even when nothing else was queued.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export function useWaitForCommit(): () => Promise<void> {
    const waitersRef = useRef<Array<() => void>>([]);
    const [requests, setRequests] = useState(0);
    useEffect(() => {
        if (waitersRef.current.length === 0) return;
        const waiters = waitersRef.current.splice(0);
        waiters.forEach((resolve) => resolve());
    }, [requests]);
    // Reads only a ref and a state setter, so it is stable for the component's life.
    return useCallback(() => new Promise<void>((resolve) => {
        waitersRef.current.push(resolve);
        setRequests((count) => count + 1);
    }), []);
}
